import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Upload, X, Sparkles, Loader2, Users, Shirt, TreePine, Package, Send, RefreshCw, Pencil, Image as ImageIcon, Maximize2, RotateCcw } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Poll a montage task until done/failed (3s interval, 5min max)
// - Retries up to 3 consecutive 404s before giving up (handles brief backend hot-reloads)
// - Network blips are retried indefinitely within the poll window
async function pollMontageTask(taskId, token, onProgress) {
  const maxPolls = 100;
  let consecutive404 = 0;
  for (let i = 0; i < maxPolls; i++) {
    await new Promise(r => setTimeout(r, 3000));
    let data;
    try {
      const resp = await axios.get(`${API}/api/recipes/montage-task/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 10000,
      });
      data = resp.data;
      consecutive404 = 0;
    } catch (err) {
      if (err.response?.status === 404) {
        consecutive404 += 1;
        if (consecutive404 >= 3) throw new Error('Task not found — backend may have restarted. Please retry.');
        continue;
      }
      // Network blip — keep polling
      continue;
    }
    if (onProgress) onProgress(data);
    if (data.status === 'done') return data;
    if (data.status === 'failed') throw new Error(data.error || 'Task failed');
  }
  throw new Error('Task timed out — please try again');
}

const SLOTS = [
  { key: 'cast', label: 'Cast', icon: Users, color: '#EC4899', desc: 'Character' },
  { key: 'costume', label: 'Costume', icon: Shirt, color: '#F59E0B', desc: 'Wardrobe' },
  { key: 'environment', label: 'Corner', icon: TreePine, color: '#10B981', desc: 'Location' },
  { key: 'sku', label: 'SKU', icon: Package, color: '#8B5CF6', desc: 'Product' },
];

export default function MontageVideoInputs({ values, onChange, token, onReprocessSlot }) {
  const [expanded, setExpanded] = useState(null); // 'cast', 'costume', etc. for describe
  const [editing, setEditing] = useState(null);   // 'cast', etc. for iteration
  const [generating, setGenerating] = useState({});
  const [editFeedback, setEditFeedback] = useState('');
  const [editRef, setEditRef] = useState(null);    // { file, preview }
  const [lightbox, setLightbox] = useState(null);  // { src, label }

  const fileRefs = {
    cast: useRef(null),
    costume: useRef(null),
    environment: useRef(null),
    sku: useRef(null),
  };
  const editRefInput = useRef(null);

  const updateSlot = (key, patch) => {
    onChange({ ...values, [key]: { ...values[key], ...patch } });
  };

  const handleFileSelect = (key, e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      updateSlot(key, { file, preview: ev.target.result, text: '', processedUrl: null });
      setExpanded(null);
      setEditing(null);
    };
    reader.readAsDataURL(file);
  };

  const clearSlot = (key) => {
    updateSlot(key, { file: null, preview: null, processedUrl: null, text: '' });
    if (fileRefs[key].current) fileRefs[key].current.value = '';
  };

  const generateFromText = async (key) => {
    const text = values[key]?.text?.trim();
    if (!text) return;
    setGenerating(g => ({ ...g, [key]: true }));
    try {
      const { data } = await axios.post(`${API}/api/recipes/generate-slot-image`, { text, slot_type: key }, {
        headers: { Authorization: `Bearer ${token}` }, timeout: 60000,
      });
      if (!data.task_id) throw new Error('No task_id returned');
      const result = await pollMontageTask(data.task_id, token);
      if (result.url) {
        try {
          const resp = await fetch(result.url);
          const blob = await resp.blob();
          const dataUrl = await new Promise(r => { const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(blob); });
          updateSlot(key, { preview: dataUrl, processedUrl: result.url, file: null });
        } catch { updateSlot(key, { preview: result.url, processedUrl: result.url, file: null }); }
        setExpanded(null);
      }
    } catch (err) { alert(err.message || 'Generation failed'); }
    finally { setGenerating(g => ({ ...g, [key]: false })); }
  };

  const iterateImage = async (key) => {
    if (!editFeedback.trim()) return;
    const slot = values[key] || {};
    const imageUrl = slot.processedUrl || '';

    setGenerating(g => ({ ...g, [key]: true }));
    try {
      const formData = new FormData();
      formData.append('feedback', editFeedback.trim());
      formData.append('slot_type', key);
      if (imageUrl) formData.append('image_url', imageUrl);
      if (!imageUrl && slot.file) formData.append('image', slot.file);
      if (editRef?.file) formData.append('ref_image', editRef.file);

      const { data } = await axios.post(`${API}/api/recipes/iterate-slot-image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }, timeout: 60000,
      });
      if (!data.task_id) throw new Error('No task_id returned');
      const result = await pollMontageTask(data.task_id, token);
      if (result.url) {
        try {
          const resp = await fetch(result.url);
          const blob = await resp.blob();
          const dataUrl = await new Promise(r => { const reader = new FileReader(); reader.onload = () => r(reader.result); reader.readAsDataURL(blob); });
          updateSlot(key, { preview: dataUrl, processedUrl: result.url, file: null });
        } catch { updateSlot(key, { preview: result.url, processedUrl: result.url, file: null }); }
        setEditFeedback('');
        setEditRef(null);
        setEditing(null);
      }
    } catch (err) { alert(err.message || 'Iteration failed'); }
    finally { setGenerating(g => ({ ...g, [key]: false })); }
  };

  return (
    <div data-testid="montage-video-inputs">
      <div className="grid grid-cols-2 gap-2">
        {SLOTS.map(({ key, label, icon: Icon, color }) => {
          const slot = values[key] || {};
          const isGenerating = generating[key];
          const hasImage = slot.file || slot.preview || slot.processedUrl;

          return (
            <div
              key={key}
              className="rounded-lg overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${hasImage ? color + '30' : 'rgba(255,255,255,0.06)'}` }}
              data-testid={`montage-slot-${key}`}
            >
              {/* Header */}
              <div className="flex items-center gap-1.5 px-2 py-1.5" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                <div className="w-5 h-5 rounded flex items-center justify-center flex-shrink-0" style={{ background: `${color}20` }}>
                  <Icon className="w-3 h-3" style={{ color }} />
                </div>
                <span className="text-[10px] font-medium text-white/70 truncate">{label}</span>
              </div>

              {/* Content */}
              <div className="p-1.5">
                <input type="file" ref={fileRefs[key]} accept="image/*" onChange={(e) => handleFileSelect(key, e)} className="hidden" />

                {hasImage ? (
                  <div
                    className="relative rounded overflow-hidden group"
                    data-testid={`montage-${key}-image-preview`}
                  >
                    <img
                      src={slot.preview?.startsWith('data:') ? slot.preview : (slot.processedUrl?.startsWith('http') ? slot.processedUrl : `${API}${slot.processedUrl || ''}`)}
                      alt={label}
                      className="w-full aspect-[4/3] object-cover rounded pointer-events-none select-none"
                      draggable={false}
                    />
                    {/* Transparent tap target covering entire image */}
                    <button
                      className="absolute inset-0 z-[1] bg-transparent cursor-pointer"
                      onClick={() => {
                        const src = slot.preview?.startsWith('data:') ? slot.preview : (slot.processedUrl?.startsWith('http') ? slot.processedUrl : `${API}${slot.processedUrl || ''}`);
                        setLightbox({ src, label });
                      }}
                      aria-label={`Enlarge ${label}`}
                      data-testid={`montage-${key}-enlarge-btn`}
                    />
                    <div className="absolute bottom-1 left-1 p-1 bg-black/50 rounded-full pointer-events-none z-[2]">
                      <Maximize2 className="w-2.5 h-2.5 text-white/60" />
                    </div>

                    {/* Status badge — top-left */}
                    {slot.processStatus === 'processing' && (
                      <div
                        className="absolute top-1 left-1 flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-mono uppercase tracking-wider z-[3]"
                        style={{ background: 'rgba(30,58,138,0.8)', color: '#BFDBFE', border: '1px solid rgba(96,165,250,0.5)' }}
                        data-testid={`montage-${key}-badge-processing`}
                      >
                        <Loader2 className="w-2 h-2 animate-spin" /> Processing
                      </div>
                    )}
                    {slot.processStatus === 'processed' && (
                      <div
                        className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[8px] font-mono uppercase tracking-wider z-[3]"
                        style={{ background: 'rgba(5,150,105,0.8)', color: '#D1FAE5', border: '1px solid rgba(52,211,153,0.5)' }}
                        data-testid={`montage-${key}-badge-processed`}
                        title="Refined by AI and used by the recipe"
                      >
                        Processed
                      </div>
                    )}
                    {slot.processStatus === 'failed' && (
                      <div
                        className="absolute top-1 left-1 px-1.5 py-0.5 rounded-full text-[8px] font-mono uppercase tracking-wider z-[3]"
                        style={{ background: 'rgba(185,28,28,0.85)', color: '#FEE2E2', border: '1px solid rgba(248,113,113,0.6)' }}
                        data-testid={`montage-${key}-badge-failed`}
                        title={slot.processError || 'Processing failed'}
                      >
                        Failed
                      </div>
                    )}

                    <div className="absolute top-1 right-1 flex gap-1 z-[3]">
                      {/* Re-process button — only appears on failed slots, when parent provides onReprocessSlot */}
                      {slot.processStatus === 'failed' && onReprocessSlot && (
                        <button
                          onClick={() => onReprocessSlot(key)}
                          className="p-1 rounded-full transition-colors"
                          style={{ background: 'rgba(245,158,11,0.85)', color: '#1F2937' }}
                          data-testid={`montage-${key}-reprocess`}
                          title="Retry processing this slot"
                        >
                          <RotateCcw className="w-2.5 h-2.5" />
                        </button>
                      )}
                      <button
                        onClick={() => { setEditing(editing === key ? null : key); setExpanded(null); setEditFeedback(''); setEditRef(null); }}
                        className="p-1 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
                        data-testid={`montage-${key}-edit`}
                        title="Edit with AI"
                      >
                        <Pencil className="w-2.5 h-2.5 text-white/70" />
                      </button>
                      <button
                        onClick={() => clearSlot(key)}
                        className="p-1 bg-black/60 hover:bg-black/80 rounded-full transition-colors"
                        data-testid={`montage-${key}-clear`}
                      >
                        <X className="w-2.5 h-2.5 text-white/70" />
                      </button>
                    </div>
                  </div>
                ) : isGenerating ? (
                  <div className="aspect-[4/3] rounded bg-white/[0.03] flex flex-col items-center justify-center gap-1.5">
                    <Loader2 className="w-5 h-5 animate-spin" style={{ color }} />
                    <span className="text-[8px] text-white/30">Generating...</span>
                  </div>
                ) : (
                  <div className="aspect-[4/3] rounded border border-dashed border-white/[0.08] flex flex-col items-center justify-center gap-1.5">
                    <button
                      onClick={() => fileRefs[key].current?.click()}
                      className="flex flex-col items-center gap-1 group"
                      data-testid={`montage-${key}-upload-btn`}
                    >
                      <Upload className="w-4 h-4 text-white/15 group-hover:text-white/30" />
                      <span className="text-[8px] text-white/20 group-hover:text-white/35">Upload</span>
                    </button>
                    <button
                      onClick={() => { setExpanded(expanded === key ? null : key); setEditing(null); }}
                      className="text-[8px] text-white/20 hover:text-white/40"
                      data-testid={`montage-${key}-describe-btn`}
                    >
                      <Sparkles className="w-3 h-3 inline mr-0.5 -mt-0.5" />Describe
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Describe — text-to-image generation */}
      {expanded && (
        <div className="mt-2 rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-white/50">Describe {SLOTS.find(s => s.key === expanded)?.label}</span>
            <button onClick={() => setExpanded(null)} className="p-0.5"><X className="w-3 h-3 text-white/30" /></button>
          </div>
          <div className="flex gap-2">
            <textarea
              value={values[expanded]?.text || ''}
              onChange={(e) => updateSlot(expanded, { text: e.target.value })}
              placeholder={`Describe the ${SLOTS.find(s => s.key === expanded)?.label.toLowerCase()}...`}
              rows={2}
              className="flex-1 px-2.5 py-2 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] text-white placeholder-white/20 focus:outline-none focus:border-white/[0.15] resize-none"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); generateFromText(expanded); } }}
              data-testid={`montage-${expanded}-text`}
            />
            <button
              onClick={() => generateFromText(expanded)}
              disabled={!values[expanded]?.text?.trim() || generating[expanded]}
              className="self-end p-2.5 rounded-lg transition-all disabled:opacity-30"
              style={{ background: `${SLOTS.find(s => s.key === expanded)?.color}20`, border: `1px solid ${SLOTS.find(s => s.key === expanded)?.color}30` }}
              data-testid={`montage-${expanded}-generate-btn`}
            >
              {generating[expanded] ? <Loader2 className="w-4 h-4 animate-spin text-white/40" /> : <Send className="w-4 h-4" style={{ color: SLOTS.find(s => s.key === expanded)?.color }} />}
            </button>
          </div>
        </div>
      )}

      {/* Edit/Iterate — feedback + optional ref image */}
      {editing && (
        <div className="mt-2 rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${SLOTS.find(s => s.key === editing)?.color}20` }}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] text-white/50">Edit {SLOTS.find(s => s.key === editing)?.label}</span>
            <button onClick={() => { setEditing(null); setEditFeedback(''); setEditRef(null); }} className="p-0.5"><X className="w-3 h-3 text-white/30" /></button>
          </div>
          <div className="flex gap-2">
            <textarea
              value={editFeedback}
              onChange={(e) => setEditFeedback(e.target.value)}
              placeholder="e.g. make the face more realistic, fix the hair, change background to white..."
              rows={2}
              className="flex-1 px-2.5 py-2 rounded-lg text-xs bg-white/[0.04] border border-white/[0.06] text-white placeholder-white/20 focus:outline-none focus:border-white/[0.15] resize-none"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); iterateImage(editing); } }}
              data-testid={`montage-${editing}-edit-text`}
            />
            <div className="flex flex-col gap-1 self-end">
              <input type="file" ref={editRefInput} accept="image/*" className="hidden"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = (ev) => setEditRef({ file, preview: ev.target.result });
                  reader.readAsDataURL(file);
                }}
              />
              <button
                onClick={() => editRefInput.current?.click()}
                className="p-2 rounded-lg transition-all hover:bg-white/[0.05]"
                style={{ border: '1px solid rgba(255,255,255,0.08)' }}
                title="Add reference image"
                data-testid={`montage-${editing}-ref-btn`}
              >
                <ImageIcon className="w-3.5 h-3.5 text-white/30" />
              </button>
              <button
                onClick={() => iterateImage(editing)}
                disabled={!editFeedback.trim() || generating[editing]}
                className="p-2 rounded-lg transition-all disabled:opacity-30"
                style={{ background: `${SLOTS.find(s => s.key === editing)?.color}20`, border: `1px solid ${SLOTS.find(s => s.key === editing)?.color}30` }}
                data-testid={`montage-${editing}-edit-submit`}
              >
                {generating[editing] ? <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40" /> : <Send className="w-3.5 h-3.5" style={{ color: SLOTS.find(s => s.key === editing)?.color }} />}
              </button>
            </div>
          </div>
          {editRef && (
            <div className="flex items-center gap-2 mt-1.5">
              <img src={editRef.preview} alt="ref" className="w-8 h-8 rounded object-cover" />
              <span className="text-[9px] text-white/30">Reference</span>
              <button onClick={() => { setEditRef(null); if (editRefInput.current) editRefInput.current.value = ''; }} className="p-0.5">
                <X className="w-2.5 h-2.5 text-white/30" />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Fullscreen Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(null)}
          data-testid="montage-lightbox"
        >
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors z-10"
            data-testid="montage-lightbox-close"
          >
            <X className="w-5 h-5 text-white" />
          </button>
          <span className="absolute top-5 left-4 text-xs text-white/50">{lightbox.label}</span>
          <img
            src={lightbox.src}
            alt={lightbox.label}
            className="max-w-full max-h-[85vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
